/**
 * Region visual-snapshot read client (Macroblock #17, Phase B).
 *
 * Same-origin relative URL, mirrors viewer/api.ts conventions (injectable fetcher
 * for tests, no VITE_API_URL). Read-only, public — no wallet/SIWE.
 */
import type { RegionSnapshot } from "./snapshot-version";

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export class SnapshotApiError extends Error {
  code: string;
  status: number;
  aborted: boolean;
  constructor(status: number, code: string, message: string, aborted = false) {
    super(message);
    this.code = code;
    this.status = status;
    this.aborted = aborted;
  }
}

export interface SnapshotViewport {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function makeSnapshotApi(fetcher: Fetcher = globalThis.fetch.bind(globalThis)) {
  async function region(vp: SnapshotViewport, signal?: AbortSignal): Promise<RegionSnapshot> {
    const q = new URLSearchParams();
    q.set("x", String(vp.x));
    q.set("y", String(vp.y));
    q.set("w", String(vp.w));
    q.set("h", String(vp.h));
    let resp: Response;
    try {
      resp = await fetcher(`/api/v1/snapshot/region?${q.toString()}`, { method: "GET", signal });
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === "AbortError") throw new SnapshotApiError(0, "ABORTED", "request aborted", true);
      throw new SnapshotApiError(0, "NETWORK", (err as Error).message || "network");
    }
    const text = await resp.text();
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }
    if (!resp.ok) {
      const b = (body as { code?: string; message?: string }) || {};
      throw new SnapshotApiError(resp.status, b.code || `HTTP_${resp.status}`, b.message || "http error");
    }
    return body as RegionSnapshot;
  }
  return { region };
}

export type SnapshotApi = ReturnType<typeof makeSnapshotApi>;
